/**
 * Per `fan-affinity-v2` capability — append-only log of each affinity
 * analysis decision. One row per (fanId, characterId, analyzedAt).
 * Stored in `nx-fan-affinity-analysis-log-<env>` with PK=`fanId`,
 * SK=`<characterId>#<analyzedAt>` (90-day TTL on `expiresAt`).
 *
 * The `reasoning` field captures the LLM's free-text justification.
 * It MUST NOT be exposed via any operator-readable UI (see
 * fan-affinity-v2 spec) — keep it server-side only, queryable via
 * CloudWatch / DDB console for platform-team debugging.
 */
import { z } from 'zod';
export const AffinityAnalysisLogRowSchema = z.object({
    fanId: z.string().min(1),
    characterId: z.string().min(1),
    /** ISO datetime when the analyzer Lambda processed this pair. */
    analyzedAt: z.string().datetime(),
    /** Score BEFORE applying delta — for audit traceability. */
    previousScore: z.number().int().min(0).max(100),
    /** Delta applied AFTER server-side clamp to [-2, +2]. */
    delta: z.number().int().min(-2).max(2),
    /** Score AFTER applying delta (clamped to [0, 100]). */
    newScore: z.number().int().min(0).max(100),
    /** Number of fan turns the LLM considered (capped at 20 in handler). */
    turnCountAnalyzed: z.number().int().min(0),
    /** Total tokens billed by the LLM provider for this analysis call. */
    llmTokensUsed: z.number().int().min(0),
    /** End-to-end latency of the LLM round trip in milliseconds. */
    llmCallLatencyMs: z.number().int().min(0),
    /** LLM's natural-language justification. Server-side audit only —
     *  MUST NOT leak to operator UI (prompt-injection content shield). */
    reasoning: z.string().max(2000),
    /** Unix epoch seconds; DDB TTL reaps after 90 days. */
    expiresAt: z.number().int().positive(),
});
